import type { PropertyCursor, PropertySortDirection, RoomSortBy } from '@/api/core/properties';
import {
  isValidMediaTarget,
  MEDIA_TARGET_KINDS,
  type MediaTarget,
} from '@/api/core/property-media';
import { hasAsciiControlOrSpace, isOffsetDateTime, isUuid } from '@/lib/validation';

const roomSortField = (value: string | null): RoomSortBy | undefined => {
  if (value === 'room_no' || value === 'floor') return value;
  return undefined;
};
const sortDirectionValue = (value: string | null): PropertySortDirection | undefined => {
  if (value === 'asc' || value === 'desc') return value;
  return undefined;
};

export function propertyListState(params: URLSearchParams): {
  valid: boolean;
  cursor: PropertyCursor | null;
  sort: { sortBy: 'name'; sortDirection: PropertySortDirection } | null;
  sortCursor: string | null;
  q: string;
} {
  const time = params.get('before_created_at');
  const id = params.get('before_property_id');
  const sortBy = params.get('sort_by');
  const sortDirection = params.get('sort_direction');
  const sortCursor = params.get('cursor');
  const duplicate = [
    'before_created_at',
    'before_property_id',
    'q',
    'sort_by',
    'sort_direction',
    'cursor',
  ].some((key) => params.getAll(key).length > 1);
  const hasSort = sortBy !== null || sortDirection !== null;
  const sortValid =
    (sortBy === null && sortDirection === null && sortCursor === null) ||
    (sortBy === 'name' &&
      (sortDirection === 'asc' || sortDirection === 'desc') &&
      (sortCursor === null || sortCursor.length > 0));
  const valid =
    !duplicate &&
    sortValid &&
    ((time === null && id === null) || (isOffsetDateTime(time) && isUuid(id))) &&
    !(hasSort && (time !== null || id !== null));
  return {
    valid,
    cursor:
      valid && time !== null && id !== null
        ? { before_created_at: time, before_property_id: id }
        : null,
    sort:
      valid && sortBy === 'name' && (sortDirection === 'asc' || sortDirection === 'desc')
        ? { sortBy, sortDirection }
        : null,
    sortCursor: valid && hasSort ? sortCursor : null,
    q: params.get('q') ?? '',
  };
}
/** 상세의 복귀 링크는 검증한 내부 목록 경로만 허용한다. */
export function propertyReturnTo(value: string | null): string {
  if (
    !value ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    value.includes('\u007f') ||
    hasAsciiControlOrSpace(value)
  )
    return '/properties';
  try {
    const url = new URL(value, 'https://internal.invalid');
    if (
      url.origin !== 'https://internal.invalid' ||
      url.pathname !== '/properties' ||
      url.hash ||
      !propertyListState(url.searchParams).valid ||
      [...url.searchParams.keys()].some(
        (key) =>
          ![
            'q',
            'before_created_at',
            'before_property_id',
            'sort_by',
            'sort_direction',
            'cursor',
          ].includes(key),
      )
    )
      return '/properties';
    return url.pathname + url.search;
  } catch {
    return '/properties';
  }
}
/** 공간 미디어 탭이 주소에 두는 상태 — 대상·펼친 그룹. 누적된 목록의 cursor는 두지 않는다. */
export const PROPERTY_MEDIA_PARAMS = ['target_kind', 'target_id', 'floor', 'group_id'] as const;
/** 지점 상세가 읽는 모든 쿼리 키 — 복귀 주소(returnTo)를 거를 때 쓴다. */
export const PROPERTY_DETAIL_PARAMS = [
  'tab',
  'room_cursor',
  'room_sort_cursor',
  'sort_by',
  'sort_direction',
  'returnTo',
  ...PROPERTY_MEDIA_PARAMS,
] as const;

export interface PropertyMediaState {
  /** 주소가 약속한 모양인가 — 아니면 아래 값은 기본값이다. */
  valid: boolean;
  target: MediaTarget;
  /** 항목을 펼친 그룹. 대상이 바뀌면 지운다. */
  groupId: string | null;
}

const DEFAULT_MEDIA_TARGET: MediaTarget = { kind: 'PROPERTY', id: null, floor: null };

/** `?tab=media` 주소 상태. 선택자 조합은 서버 규칙(`isValidMediaTarget`)과 같아야 한다. */
export function propertyMediaState(params: URLSearchParams): PropertyMediaState {
  const fallback: PropertyMediaState = {
    valid: false,
    target: DEFAULT_MEDIA_TARGET,
    groupId: null,
  };
  if (PROPERTY_MEDIA_PARAMS.some((key) => params.getAll(key).length > 1)) return fallback;
  const kindParam = params.get('target_kind');
  const kind = kindParam === null ? 'PROPERTY' : MEDIA_TARGET_KINDS.find((k) => k === kindParam);
  const floorParam = params.get('floor');
  const groupId = params.get('group_id');
  const floor =
    floorParam === null ? null : /^-?\d{1,9}$/.test(floorParam) ? Number(floorParam) : NaN;
  if (kind === undefined || Number.isNaN(floor) || !(groupId === null || isUuid(groupId)))
    return fallback;
  // "-0"은 0과 같은 층이다.
  const target: MediaTarget = {
    kind,
    id: params.get('target_id'),
    floor: floor === 0 ? 0 : floor,
  };
  return isValidMediaTarget(target) ? { valid: true, target, groupId } : fallback;
}

/** 대상·그룹을 주소에 반영한다. 기본값(지점)은 키를 지워 주소를 짧게 둔다. */
export function withPropertyMedia(
  params: URLSearchParams,
  patch: { target?: MediaTarget; groupId?: string | null },
): URLSearchParams {
  const next = new URLSearchParams(params);
  next.set('tab', 'media');
  if (patch.target) {
    next.delete('target_kind');
    next.delete('target_id');
    next.delete('floor');
    // 다른 대상의 그룹은 의미가 없다.
    next.delete('group_id');
    if (patch.target.kind !== 'PROPERTY') next.set('target_kind', patch.target.kind);
    if (patch.target.id !== null) next.set('target_id', patch.target.id);
    if (patch.target.floor !== null) next.set('floor', String(patch.target.floor));
  }
  if (patch.groupId !== undefined) {
    if (patch.groupId === null) next.delete('group_id');
    else next.set('group_id', patch.groupId);
  }
  return next;
}

export function propertyDetailState(params: URLSearchParams) {
  const roomCursor = params.get('room_cursor');
  const cursorParams = ['room_cursor'];
  const sortBy = params.get('sort_by');
  const sortDirection = params.get('sort_direction');
  const roomSortCursor = params.get('room_sort_cursor');
  const hasSort = sortBy !== null || sortDirection !== null;
  const sortValid =
    (sortBy === null && sortDirection === null && roomSortCursor === null) ||
    ((sortBy === 'room_no' || sortBy === 'floor') &&
      (sortDirection === 'asc' || sortDirection === 'desc') &&
      (roomSortCursor === null || roomSortCursor.length > 0));
  const media = propertyMediaState(params);
  return {
    valid:
      cursorParams.every((key) => params.getAll(key).length <= 1) &&
      ['sort_by', 'sort_direction', 'room_sort_cursor'].every(
        (key) => params.getAll(key).length <= 1,
      ) &&
      sortValid &&
      media.valid &&
      !(hasSort && roomCursor !== null) &&
      !(roomSortCursor !== null && !hasSort) &&
      (roomCursor === null || isUuid(roomCursor)),
    cursors: {
      room: roomCursor,
      roomSort: roomSortCursor,
      sortBy: roomSortField(sortBy),
      sortDirection: sortDirectionValue(sortDirection),
    },
    media,
    returnTo: propertyReturnTo(params.get('returnTo')),
  };
}
export function propertyDetailHref(propertyId: string, returnTo: string) {
  return `/properties/${propertyId}?${new URLSearchParams({ returnTo: propertyReturnTo(returnTo) })}`;
}
